import type { CustomFieldDefinition } from '../custom-field.types'
import { COLUMN_PRESETS, getFieldTypeWidth } from '../column-widths'
import { CustomCollectionEntry } from '../custom-collection.types'
import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition } from '../module'

/**
 * Standard column definitions for custom collection entries
 */
export const CUSTOM_COLLECTION_ENTRY_STANDARD_COLUMNS: ModuleColumnDefinition[] =
  [
    {
      id: 'name',
      name: 'name' as keyof CustomCollectionEntry,
      label: 'Name',
      description: 'Entry name',
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: true,
      order: 0,
      fieldType: FieldTypeEnum.enum.STRING,
      ...COLUMN_PRESETS.NAME,
    },
    {
      id: 'createdAt',
      name: 'createdAt' as keyof CustomCollectionEntry,
      label: 'Created',
      description: 'Date when the entry was created',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 98,
      fieldType: FieldTypeEnum.enum.DATETIME,
      dateOnly: true,
      ...COLUMN_PRESETS.CREATED_DATE,
    },
    {
      id: 'updatedAt',
      name: 'updatedAt' as keyof CustomCollectionEntry,
      label: 'Updated',
      description: 'Date when the entry was last updated',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 99,
      fieldType: FieldTypeEnum.enum.DATETIME,
      ...COLUMN_PRESETS.UPDATED_DATE,
    },
  ]

/**
 * Returns the standard collection entry columns, optionally overriding the
 * label of the built-in primary "Name" column. The underlying field stays
 * `name`; only the display label changes.
 */
export function getCustomCollectionEntryStandardColumns(
  nameFieldLabel?: string,
): ModuleColumnDefinition[] {
  if (!nameFieldLabel?.trim()) {
    return CUSTOM_COLLECTION_ENTRY_STANDARD_COLUMNS
  }

  return CUSTOM_COLLECTION_ENTRY_STANDARD_COLUMNS.map(column =>
    column.id === 'name' ? { ...column, label: nameFieldLabel.trim() } : column,
  )
}

/**
 * Helper to combine standard and custom fields for collection entries
 */
export function getCustomCollectionEntryColumns(
  customFields: CustomFieldDefinition[] = [],
  visibilityPreferences?: Record<string, boolean>,
  nameFieldLabel?: string,
): ModuleColumnDefinition[] {
  const standardColumns = getCustomCollectionEntryStandardColumns(
    nameFieldLabel,
  ).map(column => ({
    ...column,
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id]
        : column.isVisible,
  }))

  const customColumns = customFields.map((field, index) => {
    const widthSettings = getFieldTypeWidth(field.fieldType)
    const id = `custom_${field.fieldName}`

    return {
      id,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[id] !== undefined
          ? !!visibilityPreferences[id]
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 90 + index,
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
    }
  }) as ModuleColumnDefinition[]

  return [...standardColumns, ...customColumns]
}
